import { useState, useEffect, useCallback } from 'react';
import { PrayerRequest, PrayerStatus, PrayerType, PrayerList } from '../types';

const defaultListId = '1';
const now = new Date().toISOString();

const initialLists: PrayerList[] = [
    { id: defaultListId, name: 'General', updatedAt: now },
    { id: '2', name: 'Family', updatedAt: now },
];

const initialPrayers: PrayerRequest[] = [
    {
        id: '1',
        listId: '2',
        requestor: 'Me',
        date: new Date().toISOString(),
        title: 'Guidance on a new project',
        type: PrayerType.PETITION,
        status: PrayerStatus.CURRENT,
        details: 'Lord, I pray for wisdom and clarity as I begin this new project at work. Guide my steps and help me to honor you in all I do.',
        bibleVerseRecommendation: 'Proverbs 3:5-6 - "Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight."',
        updatedAt: now,
    },
    {
        id: '2',
        listId: defaultListId,
        requestor: 'Jane Doe',
        date: new Date(Date.now() - 86400000 * 5).toISOString(),
        title: 'Healing for her mother',
        type: PrayerType.INTERCESSION,
        status: PrayerStatus.ANSWERED,
        details: 'Praying for complete healing for Jane\'s mother, who is recovering from surgery.',
        answeredDetails: 'The surgery was successful and she is recovering faster than expected! Praise God.',
        bibleVerseRecommendation: 'Isaiah 41:10 - "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand."',
        updatedAt: now,
    }
];

export const usePrayerJournal = () => {
    const [prayers, setPrayers] = useState<PrayerRequest[]>(() => {
        try {
            const storedPrayers = window.localStorage.getItem('prayerJournal');
            return storedPrayers ? JSON.parse(storedPrayers) : initialPrayers;
        } catch (error) {
            return initialPrayers;
        }
    });

    const [lists, setLists] = useState<PrayerList[]>(() => {
        try {
            const storedLists = window.localStorage.getItem('prayerJournalLists');
            return storedLists ? JSON.parse(storedLists) : initialLists;
        } catch (error) {
            return initialLists;
        }
    });

    useEffect(() => {
        window.localStorage.setItem('prayerJournal', JSON.stringify(prayers));
    }, [prayers]);

    useEffect(() => {
        window.localStorage.setItem('prayerJournalLists', JSON.stringify(lists));
    }, [lists]);

    const addPrayer = useCallback((newPrayerData: Omit<PrayerRequest, 'id' | 'date' | 'status' | 'updatedAt'>) => {
        const now = new Date().toISOString();
        const newPrayer: PrayerRequest = {
            ...newPrayerData,
            id: new Date().getTime().toString(),
            date: now,
            status: PrayerStatus.CURRENT,
            updatedAt: now,
        };
        setPrayers(prev => [newPrayer, ...prev]);
        return newPrayer;
    }, [setPrayers]);

    const updatePrayer = useCallback((id: string, updates: Partial<Omit<PrayerRequest, 'id'>>) => {
        const now = new Date().toISOString();
        setPrayers(prev =>
            prev.map(p => (p.id === id ? { ...p, ...updates, updatedAt: now } : p))
        );
    }, [setPrayers]);
    
    const deletePrayer = useCallback((id: string) => {
        setPrayers(prev => prev.filter(p => p.id !== id));
    }, [setPrayers]);

    const findPrayerByTitle = useCallback((title: string): PrayerRequest | undefined => {
        const lowercasedTitle = title.toLowerCase().trim();
        return prayers.find(p => p.title.toLowerCase().trim() === lowercasedTitle);
    }, [prayers]);

    const findListByName = useCallback((name: string): PrayerList | undefined => {
        const lowercasedName = name.toLowerCase().trim();
        return lists.find(l => l.name.toLowerCase().trim() === lowercasedName);
    }, [lists]);

    const addList = useCallback((name: string) => {
        if(lists.some(l => l.name.toLowerCase() === name.toLowerCase())) return;
        const newList: PrayerList = {
            id: new Date().getTime().toString(),
            name,
            updatedAt: new Date().toISOString(),
        };
        setLists(prev => [...prev, newList]);
        return newList;
    }, [lists, setLists]);

    const updateList = useCallback((id: string, name: string) => {
        const now = new Date().toISOString();
        setLists(prev => prev.map(l => l.id === id ? { ...l, name, updatedAt: now } : l));
    }, [setLists]);

    const deleteList = useCallback((id: string) => {
        if (id === defaultListId) return; // Cannot delete default list
        // Reassign prayers from the deleted list to the default list
        setPrayers(prev => prev.map(p => p.listId === id ? { ...p, listId: defaultListId } : p));
        setLists(prev => prev.filter(l => l.id !== id));
    }, [setPrayers, setLists]);
    
    const setJournalState = useCallback((newState: { prayers: PrayerRequest[], lists: PrayerList[] }) => {
        setPrayers(newState.prayers || []);
        setLists(newState.lists || []);
    }, [setPrayers, setLists]);

    return {
        prayers,
        lists,
        addPrayer,
        updatePrayer,
        deletePrayer,
        findPrayerByTitle,
        findListByName,
        addList,
        updateList,
        deleteList,
        setJournalState
    };
};